import apiClient from '../client';

export interface MonthlySalesRow {
  Year: number;
  Month: number;
  Revenue: number;
  OrderCount: number;
}

export interface DailySalesRow {
  Year: number;
  Month: number;
  Day: number;
  Revenue: number;
  OrderCount: number;
}

export type ProductType = 'Seat Cover' | 'Dash Cover' | 'Cargo Liner';

export interface ProductTypeSalesRow {
  Year: number;
  Month: number;
  Day: number;
  ProductType: ProductType;
  Revenue: number;
  Units: number;
  OrderCount: number;
}

export interface CustomerSalesRow {
  CustomerName: string;
  CustomerId: string;
  Year1Revenue: number;
  Year1Orders: number;
  Year2Revenue: number;
  Year2Orders: number;
}

export interface FabricSalesRow {
  Material: string;
  Year1Revenue: number;
  Year1Orders: number;
  Year2Revenue: number;
  Year2Orders: number;
}

export interface CustomerSummaryRow {
  CustomerName: string;
  Year: number;
  TotalRevenue: number;
  TotalOrders: number;
  AvgOrderValue: number;
}

export interface CustomerTopProductRow {
  PartNumber: string;
  FabricName: string;
  OrderCount: number;
  Revenue: number;
}

export interface CustomerReportData {
  customerName: string;
  summary: CustomerSummaryRow[];
  monthlySales: MonthlySalesRow[];
  topProducts: CustomerTopProductRow[];
}

export interface CitySalesRow {
  City: string;
  Revenue: number;
  OrderCount: number;
}

export interface StateSalesRow {
  State: string;
  Year1Revenue: number;
  Year1Orders: number;
  Year2Revenue: number;
  Year2Orders: number;
}

export interface TopProductRow {
  PartNumber: string;
  FabricName: string;
  OrderCount: number;
  Revenue: number;
}

export interface RevenueSummaryRow {
  Year: number;
  TotalRevenue: number;
  TotalOrders: number;
  AvgOrderValue: number;
}

export const businessDashboardService = {
  getRevenueSummary: async (year1: number, year2: number): Promise<RevenueSummaryRow[]> => {
    const res = await apiClient.get('/business-dashboard/summary', {
      params: { year1, year2 },
    });
    return res.data;
  },


  getDailySales: async (year1: number, year2: number, fromMonth: number, toMonth: number, fromDay?: number, toDay?: number): Promise<DailySalesRow[]> => {
    const res = await apiClient.get('/business-dashboard/daily-sales', {
      params: { year1, year2, fromMonth, toMonth, fromDay, toDay },
    });
    return res.data;
  },

  getMonthlySales: async (year1: number, year2: number): Promise<MonthlySalesRow[]> => {
    const res = await apiClient.get('/business-dashboard/monthly-sales', {
      params: { year1, year2 },
    });
    return res.data;
  },

  getSalesByProductType: async (year1: number, year2: number, fromMonth: number, toMonth: number, fromDay?: number, toDay?: number): Promise<ProductTypeSalesRow[]> => {
    const res = await apiClient.get('/business-dashboard/sales-by-product-type', {
      params: { year1, year2, fromMonth, toMonth, fromDay, toDay },
    });
    return res.data;
  },

  getSalesByCity: async (state: string, year: number, month: number): Promise<CitySalesRow[]> => {
    const res = await apiClient.get('/business-dashboard/sales-by-city', {
      params: { state, year: year || 0, month },
    });
    return res.data;
  },

  getSalesByState: async (year1: number, year2: number, month: number): Promise<StateSalesRow[]> => {
    const res = await apiClient.get('/business-dashboard/sales-by-state', {
      params: { year1, year2, month },
    });
    return res.data;
  },

  getSalesByCustomer: async (year1: number, year2: number, month: number, limit = 25): Promise<CustomerSalesRow[]> => {
    const res = await apiClient.get('/business-dashboard/sales-by-customer', {
      params: { year1, year2, month, limit },
    });
    return res.data;
  },

  getSalesByFabric: async (
    year1: number,
    year2: number,
    month: number,
    limit = 25,
    fromMonth?: number,
    toMonth?: number,
    fromDay?: number,
    toDay?: number,
  ): Promise<FabricSalesRow[]> => {
    const res = await apiClient.get('/business-dashboard/sales-by-fabric', {
      params: { year1, year2, month, limit, fromMonth, toMonth, fromDay, toDay },
    });
    return res.data;
  },

  getTopProducts: async (year: number, limit = 20): Promise<TopProductRow[]> => {
    const res = await apiClient.get('/business-dashboard/top-products', {
      params: { year: year || 0, limit },
    });
    return res.data;
  },

  getCustomerDailySales: async (customerId: string, year1: number, year2: number, fromMonth: number, toMonth: number, fromDay?: number, toDay?: number): Promise<DailySalesRow[]> => {
    const res = await apiClient.get('/business-dashboard/customer-daily-sales', {
      params: { customerId, year1, year2, fromMonth, toMonth, fromDay, toDay },
    });
    return res.data;
  },

  getCustomerReport: async (customerId: string, year1: number, year2: number): Promise<CustomerReportData> => {
    const res = await apiClient.get('/business-dashboard/customer-report', {
      params: { customerId, year1, year2 },
    });
    return res.data;
  },
};
